import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import {
  fetchTimeline,
  getHeaderBounds,
  getSessionProperties,
  handleDownloadDiagnostics,
  handleDownloadTranscript,
} from "./SessionConsoleHelpers";
import AssignmentTabView from "./AssignmentTabView";
import PersonaTabView from "./PersonaTabView";
import ConversationProcessingJobsTabView from "./ConversationProcessingJobsTabView";
import VoiceStatisticsExportModal from "./VoiceStatisticsExportModal";
import SessionSidebar from "./SessionSidebar";
import SessionTimeline, { SIDE_LENGTH } from "./SessionTimeline";
import { validate as isValidUUID } from "uuid";
import EventTimelineView from "./EventTimelineView";
import CustomAudioPlayer from "../common/CustomAudioPlayer";
import sessionService from "../../services/sessionService";
import SearchBox from "../common/SearchBox";
import { SESSION_DISPLAY_STATUS_OPTIONS } from "../../utils/sessionDisplayStatus";
import { FaDownload, FaSpinner, FaEllipsisV } from "react-icons/fa";
import { useToast } from "../common/ToastContext";
import TabbedView from "../common/TabbedView";
import { getCSVExport } from "../../services/exportService";
import { API_URL } from "../../services/config";

const SESSIONS_PER_PAGE = 100;
const IdTypes = Object.freeze({
  SESSION: Symbol("session"),
  ASSIGNMENT: Symbol("assignment"),
  UNKNOWN: Symbol("unknown"),
  NOTFOUND: Symbol("not found"),
});

const DATE_ONLY_PARAM = /^\d{4}-\d{2}-\d{2}$/;

/** Format a Date as YYYY-MM-DD. Uses local parts; toISOString would shift the day. */
const toDateParam = (date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");

/** Parse the date param. Bare YYYY-MM-DD parses as UTC, so pin it to local midnight. */
const fromDateParam = (value) =>
  new Date(DATE_ONLY_PARAM.test(value) ? `${value}T00:00:00` : value);

const SessionConsole = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const [timeline, setTimeline] = useState({
    bounds: { startDate: null, endDate: null },
    data: [],
  });
  const [totalSelectedSessions, setTotalSelectedSessions] = useState(0);
  const timelineRef = useRef(null);

  const [modalityFilter, setModalityFilter] = useState();
  const [statusFilter, setStatusFilter] = useState(null);
  const [activeSort, setActiveSort] = useState({
    field: null,
    direction: null,
  });

  const [sessionEvents, setSessionEvents] = useState({
    session: null,
    events: [],
  });
  const [headers, setHeaders] = useState([]);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [showWaveform, setShowWaveform] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showEvents, setShowEvents] = useState(false);
  const [renderedTabIds, setRenderedTabIds] = useState([]);
  const [expandedProperties, setExpandedProperties] = useState({});
  const [showExportModal, setShowExportModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [windowResizing, setWindowResizing] = useState(false);
  const [userTyping, setUserTyping] = useState(false);
  const [earliestDate, setEarliestDate] = useState(null);

  // Process the link information ahead of time to reduce re-loading content.
  const sessionIdParam = searchParams.get("id") || null;
  const assignmentIdParam = searchParams.get("assignment_id") || null;
  const dateParam = searchParams.get("date") || null;
  const searchTerm = searchParams.get("searchTerm") || null;

  // Setting the session ID or assignment ID _should_ trigger the reload of the session data.
  let initSessionId = null;
  let initAssignmentId = null;
  let initSelectedDate = new Date();
  let initSearchQuery = { idValue: null, idType: IdTypes.UNKNOWN };

  let mustInitWithSessionData = true;
  if (sessionIdParam) {
    initSessionId = sessionIdParam;
  } else if (assignmentIdParam) {
    initAssignmentId = assignmentIdParam;
  } else {
    mustInitWithSessionData = false;
  }

  if (searchTerm) {
    initSearchQuery = { idValue: searchTerm, idType: IdTypes.UNKNOWN };
  }

  if (dateParam) {
    initSelectedDate = fromDateParam(dateParam);
  }

  // Set defaults for remaining select date content.
  const [newSessionSelected, setNewSessionSelected] = useState(
    initSessionId || initAssignmentId,
  );
  const [initWithSessionData, setInitWithSessionData] = useState(
    mustInitWithSessionData,
  );
  const [selectedDate, setSelectedDate] = useState(initSelectedDate);
  const [assignmentId, setAssignmentId] = useState(initAssignmentId);
  const [sessionId, setSessionId] = useState(initSessionId);
  const [sessionModality, setSessionModality] = useState(null);
  const [searchQuery, setSearchQuery] = useState(initSearchQuery);

  // Compute indices based on the current page.
  const startIndex = () => (currentPage - 1) * SESSIONS_PER_PAGE;
  const endIndex = () =>
    Math.min(startIndex() + SESSIONS_PER_PAGE, totalSelectedSessions);

  /**
   * Fetch the data for the main session & transcript display whenever the search query changes.
   * This is only run when we have an exact session or assignment ID (select a single session). All other search queries
   * should register as a no-op (besides potentially clearing the display).
   */
  useEffect(() => {
    const fetchSessionData = async () => {
      if (!newSessionSelected) return; // This prevents duplicate calls.
      setNewSessionSelected(false);

      if (!sessionId && !assignmentId) {
        // Clear the display field.
        clearSessionDisplay();
      } else {
        // Fill the display field.
        try {
          let session;
          if (sessionId) {
            session = await fetchSessionFromEitherId(
              sessionId,
              IdTypes.SESSION,
            );
          } else {
            // if assignment ID
            session = await fetchSessionFromEitherId(
              assignmentId,
              IdTypes.ASSIGNMENT,
            );
          }
          if (session) {
            // Set the session ID in case this was triggered by an assignment ID.
            if (sessionId !== session.id) setSessionId(session.id);

            // Set the assignment ID which is used in the details tabs (Assignment and Persona).
            if (assignmentId !== session.assignment_id)
              setAssignmentId(session.assignment_id);

            if (session.modality !== sessionModality)
              setSessionModality(session.modality);

            // Set the current selected date.
            const sessionDate = new Date(
              activeSort.field === "updated_at"
                ? session.updated_at
                : session.created_at,
            );
            if (sessionDate.toDateString() !== selectedDate.toDateString())
              setSelectedDate(sessionDate);

            // Once a deep link's assignment resolves to a session, swap the URL over to
            // that session and its date so refreshing or sharing the link is stable. The
            // search box sets assignment_id too, but there the assignment is the intent.
            if (!sessionId && initWithSessionData) {
              setSearchParams(
                (prev) => {
                  const newParams = new URLSearchParams(prev);
                  newParams.delete("assignment_id");
                  newParams.set("id", session.id);
                  newParams.set("date", toDateParam(sessionDate));
                  return newParams;
                },
                { replace: true },
              );
            }

            // Set the current page based on the date and the header page bounds
            // This is really only needed when we are loading the page from a URL
            if (initWithSessionData) {
              const headerPages = await getHeaderBounds(
                sessionDate,
                activeSort,
                modalityFilter,
                [IdTypes.NOTFOUND, IdTypes.UNKNOWN].includes(searchQuery.idType)
                  ? searchQuery.idValue
                  : null,
                SESSIONS_PER_PAGE,
              );
              const sessionPage = headerPages.find(
                (b) =>
                  new Date(b.min_datetime) <= sessionDate &&
                  sessionDate <= new Date(b.max_datetime),
              );
              if (!sessionPage) {
                console.warn("Could not find page for session.");
              } else {
                setCurrentPage(sessionPage.page_num + 1);
              }
            }
            setInitWithSessionData(false);

            // Set the transcript/events data display.
            const eventsData = await sessionService.listEvents(session.id);
            setSessionEvents({ session: session, events: eventsData });
          } else {
            console.warn(`No session found for session ID ${sessionId}`);
            clearSessionDisplay();
          }
        } catch (error) {
          console.error(`Error while fetching session: ${error}`);
          clearSessionDisplay();
        }
      }
    };
    fetchSessionData();
  }, [newSessionSelected]);

  /**
   * Keep track of when the window is resizing so we know when we need to freshly load the the timeline.
   */
  useEffect(() => {
    let timeout;
    const handleResize = () => {
      clearTimeout(timeout);
      setWindowResizing(true);
      timeout = setTimeout(() => {
        setWindowResizing(false);
      }, 200);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /**
   * Keep track of when the search bar is having content entered.
   */
  useEffect(() => {
    let timeout;
    const handleTyping = () => {
      clearTimeout(timeout);
      setUserTyping(true);
      timeout = setTimeout(() => {
        setUserTyping(false);
      }, 500);
    };
    window.addEventListener("input", handleTyping);
    return () => window.removeEventListener("input", handleTyping);
  }, []);

  /**
   * Populate the timeline.
   *
   * We do this after loading the session, in case the session adjusts the selected date time.
   * Also, re-run this whenever the window finishes resizing, or when the selected date or filters
   * change. _NOT_ effected by changes to the selected ID (not directly, though selecting a new ID may
   * in some cases trigger the selectedDate to change, triggering this).
   */
  useEffect(() => {
    const fetchTimelineData = async () => {
      if (windowResizing || userTyping) {
        return; // Wait until the window is done resizing or the user is done typing.
      }
      // Get the current page size, and set the range to fill it neatly.
      const timelineRect = timelineRef.current.getBoundingClientRect();
      const timelineRange = Math.floor(
        (timelineRect.width / SIDE_LENGTH) * 0.6,
      );

      // Get the date of the earliest session to share with the fetchTimeline function (it is
      // cached in this layer to avoid frequent re-calls).
      const earliestSessionDate = await getEarliestSessionDate();
      let fetchedTimeline = await fetchTimeline(
        selectedDate,
        earliestSessionDate,
        timelineRange,
        modalityFilter,
        [IdTypes.NOTFOUND, IdTypes.UNKNOWN].includes(searchQuery.idType)
          ? searchQuery.idValue
          : null,
        activeSort,
        statusFilter,
      );
      setTimeline(fetchedTimeline);

      // We need the current session total for paging through headers, and we get that here.
      const selectedData = fetchedTimeline.data.find(
        (timelineDate) =>
          timelineDate.date.toDateString() === selectedDate.toDateString(),
      );
      if (selectedData) {
        setTotalSelectedSessions(selectedData.total);
      }
    };
    fetchTimelineData();
  }, [
    modalityFilter,
    statusFilter,
    selectedDate,
    activeSort,
    windowResizing,
    userTyping,
    searchQuery,
  ]);

  /**
   * Session side panels data loading.
   *
   * Get session headers whenever the selected date or other filters/search parameters change, or the current page.
   */
  useEffect(() => {
    const fetchSessionHeaders = async () => {
      if (userTyping) return; // Wait for the user to finish typing.

      // Skip the query if we already know there aren't any results.
      if (totalSelectedSessions === 0) {
        setHeaders([]);
        return;
      }

      try {
        // Parse the sort field
        const orderBy = activeSort.field
          ? `${activeSort.field}:${activeSort.direction}`
          : "created_at:desc";

        // Set time range to start and end of day
        const startTime = new Date(selectedDate);
        startTime.setHours(0, 0, 0, 0);
        const endTime = new Date(selectedDate);
        endTime.setHours(23, 59, 59);

        const headers = await sessionService.getSessionHeaders(
          startTime,
          endTime,
          modalityFilter,
          [IdTypes.NOTFOUND, IdTypes.UNKNOWN].includes(searchQuery.idType)
            ? searchQuery.idValue
            : null,
          orderBy,
          SESSIONS_PER_PAGE,
          startIndex() || 0,
          statusFilter,
        );
        setHeaders(headers);
      } catch (error) {
        console.error("Error fetching all sessions for search:", error);
      }
    };

    fetchSessionHeaders();
  }, [
    modalityFilter,
    statusFilter,
    selectedDate,
    activeSort,
    currentPage,
    searchQuery,
    totalSelectedSessions,
    userTyping,
  ]);

  /**
   * Simple function to clear the main session & transcript display.
   */
  const clearSessionDisplay = () => {
    setAssignmentId(null);
    setSessionId(null);
    setSessionEvents({ session: null, events: [] });
  };

  /**
   * Get the date of the earliest available session. Used to set bounds in the timeline paging.
   */
  const getEarliestSessionDate = async () => {
    if (!earliestDate) {
      const date = await sessionService.getEarliestDate();
      setEarliestDate(date);
      return date;
    }
    return earliestDate;
  };

  /**
   * Search for a session given either an assignment ID or a session ID.
   * @param {String} givenId - the UUID to search.
   * @param {Symbol} idType  - One of the IdTypes enum values.
   * @returns {Promise<*|Session|null>}
   */
  const fetchSessionFromEitherId = async (givenId, idType) => {
    if (idType === IdTypes.SESSION) {
      const session = await sessionService.getEntry(givenId);
      if (!session) console.warn(`No session found for session ID ${givenId}`);
      return session;
    } else if (idType === IdTypes.ASSIGNMENT) {
      const sessions = await sessionService.getByAssignmentId(givenId);
      if (sessions && sessions.length > 0) {
        return sessions[0]; // Assuming you want the first session for the assignment
      } else {
        console.warn(`No sessions found for assignment ID ${givenId}`);
        return null;
      }
    } else {
      console.error(
        `Invalid ID type ${idType} provided to fetch session function.`,
      );
    }
  };

  const tabResetKey = useMemo(
    () => sessionEvents.session?.id || "no-session",
    [sessionEvents.session?.id],
  );

  const clearActiveFilter = () => {
    setModalityFilter(null);
    setStatusFilter(null);
    setCurrentPage(1);
  };

  const handleSortSelect = (option) => {
    setActiveSort((prevSort) => {
      if (prevSort.field === option.value) {
        return {
          field: option.value,
          direction: prevSort.direction === "desc" ? "asc" : "desc",
        };
      } else {
        return {
          field: option.value,
          direction: "desc",
        };
      }
    });
  };

  const clearActiveSort = () => {
    setActiveSort({
      field: null,
      direction: null,
    });
  };

  const handleDateSelection = async (dateSelection, totalSessions) => {
    // Clear any exact IDs that would match only a single entry, but leave search terms with multiple matches.
    // NOTE: yes, this can cause a bit of weird UX where complete IDs disappear while incomplete IDs or IDs that match
    //   to multiple entries (e.g. persona ID) don't. This is why this multi-tiered search set up is broken UX and
    //   should probably be replaced. Hopefully the description under the search bar can help clarify this UX a bit.
    if (
      searchQuery.idType === IdTypes.SESSION ||
      searchQuery.idType === IdTypes.ASSIGNMENT
    )
      setSearchQuery({ idValue: null, idType: null });

    // Clear session and assignment ID (we changed day, we're definitely not looking at the same session)
    setSessionId(null);
    setAssignmentId(null);
    setNewSessionSelected(true);

    // Set the new date.
    setSelectedDate(dateSelection);

    // Update the search query params. Essentially we're setting the date and clearing exact IDs (similar logic to above).
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("date", toDateParam(dateSelection));
      // Drop the exact IDs too, or a refresh would reload that session and yank the
      // view back to its own date.
      newParams.delete("id");
      newParams.delete("assignment_id");
      return newParams;
    });

    // Set some of the page header display parameters now that we're on a new page.
    setTotalSelectedSessions(totalSessions);
    setCurrentPage(1);
  };

  const handleExport = async (start_date, end_date) => {
    const fileName = `voice_statistics_${new Date().toISOString()}.csv`;
    const exportUrl = `${API_URL}api/session/voice_statistics/export.csv?start_date=${encodeURIComponent(start_date)}&end_date=${encodeURIComponent(end_date)}`;
    setIsExporting(true);
    const result = await getCSVExport({ fileName, exportUrl });
    if (result) {
      showToast(`Exported: ${fileName}`, "success");
    } else {
      showToast(`Failed to export`, "error");
    }
    setIsExporting(false);
  };

  const handleSelectId = (selectedSessionId) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      // The assignment ID only seeds the initial session; an explicit pick supersedes it.
      newParams.delete("assignment_id");
      if (selectedSessionId) {
        newParams.set("id", selectedSessionId);
      } else {
        newParams.delete("id");
      }
      return newParams;
    });
    setAssignmentId(null);
    setSessionId(selectedSessionId);
    setNewSessionSelected(true);
  };

  /**
   * Set URL parameters and reload page given the search ID.
   * @param {?string} searchTerm - either an assignment ID or a session ID. Must be a valid UUID.
   */
  const handleSearchId = (searchTerm) => {
    // If the search ID was cleared, clear the values in the URL and search bar.
    if (!searchTerm) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("searchTerm");
        return newParams;
      });
      setSearchQuery({ idValue: null, idType: null });
      return;
    }

    // Clear the session view
    setSessionId(null);
    setAssignmentId(null);
    setNewSessionSelected(true);

    // If there is a value, wait for it to be a valid UUID.
    if (!isValidUUID(searchTerm)) {
      setSearchQuery({ idValue: searchTerm, idType: IdTypes.UNKNOWN });
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("id");

        newParams.delete("assignment_id");
        newParams.set("searchTerm", searchTerm);
        return newParams;
      });
      return;
    }

    // If an exact ID is entered, there are better options than a search.
    sessionService.regularizeId(searchTerm).then((sessionId) => {
      if (!sessionId) {
        // If nothing came up, this doesn't match anything, no need to search further.
        setSearchQuery({ idValue: searchTerm, idType: IdTypes.NOTFOUND });
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("id");
          newParams.delete("assignment_id");
          newParams.set("searchTerm", searchTerm);
          return newParams;
        });
      } else if (sessionId === searchTerm) {
        // The original ID was a session ID.
        // If the ID was invalid. Assume it was intended as a session ID.
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("assignment_id");
          newParams.delete("searchTerm");
          newParams.set("id", searchTerm);
          return newParams;
        });
        setSearchQuery({ idValue: searchTerm, idType: IdTypes.SESSION });
        setSessionId(sessionId);
        setNewSessionSelected(true);
      } else {
        // The original ID was an assignment ID.
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("id");
          newParams.delete("searchTerm");
          newParams.set("assignment_id", searchTerm);
          return newParams;
        });
        setSearchQuery({ idValue: searchTerm, idType: IdTypes.ASSIGNMENT });
        setAssignmentId(searchTerm);
        setNewSessionSelected(true);
      }
    });
  };

  /**
   * Get a helpful string based on the ID type, to be displayed under the search bar.
   */
  const getHelpForIdType = () => {
    if (searchQuery.idType === IdTypes.SESSION) return "Session ID";
    if (searchQuery.idType === IdTypes.ASSIGNMENT) return "Assignment ID";
    if (searchQuery.idType === IdTypes.NOTFOUND)
      return "Search term (valid UUID)";
    if (searchQuery.idType === IdTypes.UNKNOWN && searchQuery.idValue)
      return "Search term: case insensitive & ignores dashes, underscores, and spaces.";
    return "";
  };

  const togglePropertyExpansion = useCallback((propertyKey) => {
    setExpandedProperties((prev) => ({
      ...prev,
      [propertyKey]: !prev[propertyKey],
    }));
  }, []);

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const handleShowEvents = () => {
    setShowEvents(!showEvents);
    setShowMenu(false);
  };

  const eventTimelineProps = useMemo(
    () => ({
      initialEvents: sessionEvents.events ?? [],
      showEvents,
      sessionConnectedAt: sessionEvents.session?.connected_at,
    }),
    [sessionEvents.events, showEvents, sessionEvents.session?.connected_at],
  );

  const handleDownloadAudio = async () => {
    if (recordingUrl) {
      try {
        const response = await fetch(recordingUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "audio.mp3"; // You can customize the filename here
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Download failed:", error);
        showToast("Failed to download audio", "error");
      }
    } else {
      showToast("No audio available to download", "error");
    }
    setShowMenu(false);
  };

  useEffect(() => {
    let isMounted = true;
    const fetchRecordingUrl = async () => {
      if (
        sessionEvents.session?.recording_path ||
        sessionEvents.session?.twilio_media_url
      ) {
        try {
          const url = await sessionService.fetchRecordingUrl(
            sessionEvents.session.id,
          );
          if (url && isMounted) {
            setRecordingUrl(url);
          }
        } catch (error) {
          console.error("Error fetching recording URL:", error);
        }
      } else {
        setRecordingUrl(null);
      }
    };
    fetchRecordingUrl();
    return () => {
      isMounted = false;
    };
  }, [sessionEvents?.session]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMenu && !event.target.closest(".menu-container")) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  const handleTabRender = useCallback((tabId) => {
    if (!tabId) {
      setRenderedTabIds([]);
      return;
    }
    setRenderedTabIds((prev) => [...prev, tabId]);
  }, []);

  const tabs = useMemo(() => {
    if (!sessionEvents.session || !sessionEvents.session.id) return [];
    const tabConfigs = [
      {
        id: "properties",
        label: "Properties",
        title: "Properties",
        renderContent: () =>
          getSessionProperties(
            sessionEvents.session,
            "basic",
            sessionEvents.events,
            expandedProperties,
            togglePropertyExpansion,
          ),
        alwaysRender: true,
      },
      {
        id: "advanced-properties",
        label: "Advanced",
        title: "Advanced Properties",
        renderContent: () =>
          getSessionProperties(
            sessionEvents.session,
            "advanced",
            sessionEvents.events,
            expandedProperties,
            togglePropertyExpansion,
          ),
      },
      {
        id: "assignment-details",
        label: "Assignment",
        title: "Assignments",
        renderContent: () => (
          <AssignmentTabView session={sessionEvents.session} />
        ),
      },
      {
        id: "persona-details",
        label: "Persona",
        title: "Persona",
        renderContent: () => <PersonaTabView assignmentId={assignmentId} />,
      },
      {
        id: "conversation-processing",
        label: "Jobs",
        title: "Conversation Processing Jobs",
        renderContent: () => (
          <ConversationProcessingJobsTabView session={sessionEvents.session} />
        ),
      },
    ];
    return tabConfigs.map(
      ({ id, label, title, renderContent, alwaysRender }) => ({
        id,
        label,
        content: (
          <div
            style={{ minHeight: "120px", maxHeight: "95%" }}
            className="overflow-y-auto dark-slim-scrollbar"
          >
            <h3 className="text-lg font-semibold text-white mb-4 px-2">
              {title}
            </h3>
            {(renderedTabIds.includes(id) || alwaysRender) && renderContent()}
          </div>
        ),
      }),
    );
  }, [
    sessionEvents.session,
    sessionEvents.events,
    renderedTabIds,
    expandedProperties,
    togglePropertyExpansion,
  ]);

  const handleIsWaveformComplete = (data) => {
    setShowWaveform(data);
  };

  const filterOptions = [
    {
      label: "Voice",
      value: "voice",
      onSelect: () => {
        setModalityFilter("voice");
        setStatusFilter(null);
        setCurrentPage(1);
      },
    },
    {
      label: "Text",
      value: "text",
      onSelect: () => {
        setModalityFilter("text");
        setStatusFilter(null);
        setCurrentPage(1);
      },
    },
    ...SESSION_DISPLAY_STATUS_OPTIONS.map(({ label, value }) => ({
      label,
      value,
      onSelect: () => {
        setModalityFilter(null);
        setStatusFilter(value);
        setCurrentPage(1);
      },
    })),
  ];

  const sortOptions = [
    {
      label: `Created ${activeSort.field === "created_at" ? (activeSort.direction === "desc" ? "↓" : "↑") : ""}`,
      value: "created_at",
      onSelect: () => handleSortSelect({ value: "created_at" }),
    },
    {
      label: `Last Event ${activeSort.field === "updated_at" ? (activeSort.direction === "desc" ? "↓" : "↑") : ""}`,
      value: "updated_at",
      onSelect: () => handleSortSelect({ value: "updated_at" }),
    },
  ];

  return (
    <div className="overflow-hidden">
      {/* Search Bar */}
      <div className="absolute top-4 left-[50%] translate-x-[-50%] w-[500px] z-[10000]">
        <div
          className="flex items-center justify-between"
          style={{ fontSize: 14 }}
        >
          <SearchBox
            value={searchQuery.idValue}
            onSearch={handleSearchId}
            placeholder={"Filter sessions ..."}
            filterOptions={filterOptions}
            sortOptions={sortOptions}
            activeFilter={
              filterOptions.find(
                (option) => option.value === (statusFilter || modalityFilter),
              ) || null
            }
            activeSort={sortOptions.find(
              (option) => option.value === activeSort.field,
            )}
            onFilterClear={clearActiveFilter}
            onSortClear={clearActiveSort}
            includeRegexOption={false}
          />
          <div className="ml-2 flex">
            <button
              className="p-2 border border-transparent rounded-full text-white bg-indigo-600 hover:bg-indigo-700"
              onClick={() => setShowExportModal(true)}
              title="Export all Session's Statistics"
              aria-label="Export all Session's Statistics"
              disabled={isExporting}
            >
              {isExporting ? (
                <FaSpinner className="animate-spin" />
              ) : (
                <FaDownload />
              )}
            </button>
          </div>
        </div>
        {searchQuery.idType && searchQuery.idValue && (
          <div
            className="flex items-center justify-between m-1 ml-5 mb-2 text-gray-300"
            style={{ fontSize: 12 }}
          >
            {getHelpForIdType()}
          </div>
        )}
        {showExportModal && (
          <VoiceStatisticsExportModal
            onExport={handleExport}
            onClose={() => setShowExportModal(false)}
          />
        )}
      </div>
      {/* Content Body */}
      <div className="flex flex-col h-[calc(100vh-64px)]">
        {/* Timeline */}
        <div className="h-[7vh] w-full" ref={timelineRef}>
          {timeline && (
            <SessionTimeline
              data={timeline.data}
              onDateSelection={handleDateSelection}
              selectedDate={selectedDate}
              earliestDate={timeline.bounds.earliestDate}
              startDate={timeline.bounds.startDate}
              endDate={timeline.bounds.endDate}
            />
          )}
        </div>
        {/* Panel View */}
        <div className="flex-1 h-[93vh]">
          <PanelGroup autoSaveId="session-layout" direction="horizontal">
            <Panel defaultSize={30} minSize={20} maxSize={50}>
              <SessionSidebar
                headers={headers}
                sessionsPerPage={SESSIONS_PER_PAGE}
                selectedId={sessionId}
                onSelectId={handleSelectId}
                totalSessions={totalSelectedSessions}
                currentPage={currentPage}
                onSetCurrentPage={setCurrentPage}
                startIndex={startIndex()}
                endIndex={endIndex()}
              />
            </Panel>
            <PanelResizeHandle className="flex w-[2px] items-center justify-center bg-gray-700" />
            {sessionEvents.session && sessionEvents.session.id ? (
              <>
                <Panel defaultSize={30} minSize={20}>
                  <div className="flex flex-col h-full">
                    <div className="p-4 border-b-2 border-gray-700 relative flex-shrink-0">
                      <h2 className="text-2xl font-bold text-white mb-3">
                        {sessionEvents.session.name}
                      </h2>
                      {recordingUrl && (
                        <div className={showWaveform ? "" : "hidden"}>
                          <CustomAudioPlayer
                            src={recordingUrl}
                            waveFormCompleted={handleIsWaveformComplete}
                          />
                        </div>
                      )}
                      <div className="absolute top-4 right-4 menu-container">
                        <button
                          onClick={toggleMenu}
                          className="text-white p-2 hover:bg-gray-700 rounded-full transition duration-200"
                          aria-label="Menu"
                        >
                          <FaEllipsisV />
                        </button>
                        {showMenu && (
                          <div className="absolute right-0 mt-2 w-48 bg-gray-700 rounded-md shadow-lg py-1 z-50">
                            <button
                              onClick={handleShowEvents}
                              className="block w-full text-left px-4 py-2 text-white hover:bg-gray-600"
                            >
                              {!showEvents ? (
                                <>Show Events</>
                              ) : (
                                <>Hide Events</>
                              )}
                            </button>
                            <button
                              onClick={handleDownloadAudio}
                              className={
                                showWaveform
                                  ? "block w-full text-left px-4 py-2 text-white hover:bg-gray-600"
                                  : "hidden"
                              }
                              disabled={!recordingUrl}
                            >
                              Download Audio
                            </button>
                            <button
                              onClick={() =>
                                handleDownloadTranscript({
                                  sessionId: sessionEvents.session?.id,
                                })
                              }
                              className={
                                "block w-full text-left px-4 py-2 text-white hover:bg-gray-600"
                              }
                            >
                              Download Transcript
                            </button>
                            {sessionModality && sessionModality === "voice" && (
                              <button
                                onClick={async () => {
                                  try {
                                    await handleDownloadDiagnostics({
                                      sessionId: sessionEvents.session?.id,
                                    });
                                  } catch (error) {
                                    showToast(
                                      error.message ||
                                        "Failed to download diagnostics.",
                                      "error",
                                    );
                                  }
                                }}
                                className={
                                  "block w-full text-left px-4 py-2 text-white hover:bg-gray-600"
                                }
                              >
                                Download Voice Bot Diagnostics
                              </button>

                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto dark-slim-scrollbar">
                      <div className="h-[calc(100%-50px)]">
                        <EventTimelineView
                          key={sessionEvents.session?.id || "no-session"}
                          {...eventTimelineProps}
                        />
                      </div>
                    </div>
                  </div>
                </Panel>
                <PanelResizeHandle className="flex w-[2px] items-center justify-center bg-gray-700" />
                <Panel defaultSize={30} minSize={20}>
                  <TabbedView
                    tabs={tabs}
                    sendRenderedTabIds={handleTabRender}
                    resetKey={tabResetKey}
                  />
                </Panel>
              </>
            ) : (
              <Panel defaultSize={70} minSize={20}>
                <div className="h-full w-full flex items-center justify-center">
                  <h2 className="text-3xl text-white">Select a session</h2>
                </div>
              </Panel>
            )}
          </PanelGroup>
        </div>
      </div>
    </div>
  );
};

export default SessionConsole;
